import { useState, useCallback, useRef } from "react";
import { httpGet, httpPostBlob, httpPostStream } from "../utils/httpMethods";
import { CHAT_URL, MODEL_URL } from "../api/restEndpoint";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

export const useChat = () => {
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null); // Add this line

  // Send a message to the selected AI model
  const sendMessage = useCallback(
    async (
      modelId,
      messages,
      userMessage,
      userId,
      organizationId,
      threadId,
      file,
      onStreamMessage,
      setMessages,
      tool,
      onAbort,
      personaId,
      toolSettings = null, // Renamed from youtubeSettings to be more generic
      modelSettings = null
    ) => {
      try {
        setIsChatLoading(true);
        setError(null);
        // Support both single file (backward compatibility) and multiple files
        const filesToUpload = Array.isArray(file) ? file : (file ? [file] : []);
        
        if (filesToUpload.length > 0) {
          const formData = new FormData();
          formData.append("message", userMessage);
          formData.append("userId", userId);
          if (organizationId) formData.append("organizationId", organizationId);
          if (modelId) formData.append("modelId", modelId);
          if (threadId) formData.append("threadId", threadId);
          
          // Append all files with field name "files" for multiple file support
          filesToUpload.forEach((f) => {
            formData.append("files", f);
          });
          if (tool) formData.append("tool", tool);
          if (personaId) formData.append("personaId", personaId);

          // Add model settings to FormData if provided
          if (modelSettings) {
            if (modelSettings.temperature !== undefined)
              formData.append("temperature", modelSettings.temperature);
            if (modelSettings.top_p !== undefined)
              formData.append("top_p", modelSettings.top_p);
            if (modelSettings.max_tokens !== undefined)
              formData.append("max_tokens", modelSettings.max_tokens);
            if (modelSettings.system_instructions !== undefined)
              formData.append(
                "system_instructions",
                modelSettings.system_instructions
              );
          }

          // Add tool-specific settings to FormData if provided
          if (tool === "research" && toolSettings) {
            if (toolSettings.research_mode !== undefined)
              formData.append("research_mode", toolSettings.research_mode);
            if (toolSettings.citation_style !== undefined)
              formData.append("citation_style", toolSettings.citation_style);
            if (toolSettings.web_sources !== undefined)
              formData.append("web_sources", toolSettings.web_sources);
            if (toolSettings.document_sources !== undefined)
              formData.append(
                "document_sources",
                toolSettings.document_sources
              );
            if (toolSettings.ai_reasoning !== undefined)
              formData.append("ai_reasoning", toolSettings.ai_reasoning);
          } else if (tool === "youtube" && toolSettings) {
            if (toolSettings.youtubeUrl !== undefined)
              formData.append("youtubeUrl", toolSettings.youtubeUrl);
            if (toolSettings.action !== undefined)
              formData.append("youtubeAction", toolSettings.action);
            if (toolSettings.tone !== undefined)
              formData.append("youtubeTone", toolSettings.tone);
            if (toolSettings.length !== undefined)
              formData.append("youtubeLength", toolSettings.length);
            if (toolSettings.language !== undefined)
              formData.append("language", toolSettings.language);
          }
          // Debug: log all FormData entries
          for (let pair of formData.entries()) {
            logger.debug(logger.fmt`FormData ${pair[0]}: ${pair[1]}`);
          }
          // Streaming for file uploads is not supported (browser limitation)
          const response = await httpPostBlob({
            url: `${CHAT_URL}/model`,
            payload: formData,
          });
          return response?.data?.response || "";
        }
        // Use streaming for text-only chat
        const payload = {
          message: userMessage,
          userId,
          organizationId,
          threadId,
          tool,
        };
        if (modelId) payload.modelId = modelId;
        if (personaId) payload.personaId = personaId;

        // Add model settings if provided
        if (modelSettings) {
          if (modelSettings.temperature !== undefined)
            payload.temperature = modelSettings.temperature;
          if (modelSettings.top_p !== undefined)
            payload.top_p = modelSettings.top_p;
          if (modelSettings.max_tokens !== undefined)
            payload.max_tokens = modelSettings.max_tokens;
          if (modelSettings.system_instructions !== undefined)
            payload.system_instructions = modelSettings.system_instructions;
        }

        // Add tool-specific fields based on the selected tool
        if (tool === "youtube" && toolSettings) {
          payload.youtubeUrl = toolSettings.youtubeUrl;
          // Map frontend action ('summarize'/'transcribe') to backend expected values ('summary'/'transcript')
          const actionMap = {
            summarize: "summary",
            transcribe: "transcript",
          };
          payload.youtubeAction =
            actionMap[toolSettings.action] || toolSettings.action; // 'transcript' or 'summary'
          payload.youtubeTone = toolSettings.tone; // 'friendly', 'technical', 'concise'
          payload.youtubeSummaryType =
            toolSettings.summaryType || toolSettings.length; // 'short', 'medium', 'long', 'bullets', 'chapters'
          payload.youtubeLength = toolSettings.length; // 'very_short', 'short', 'medium', 'long' (for length, separate from format)
          payload.youtubeLanguage = toolSettings.language; // Send as youtubeLanguage to match backend expectation
          payload.language = toolSettings.language; // Also send as language for compatibility
        } else if (tool === "research" && toolSettings) {
          payload.research_mode =
            toolSettings.research_mode || "simple_question";
          payload.citation_style = toolSettings.citation_style || "APA";
          payload.web_sources =
            toolSettings.web_sources !== undefined
              ? toolSettings.web_sources
              : true;
          payload.document_sources =
            toolSettings.document_sources !== undefined
              ? toolSettings.document_sources
              : false;
          payload.ai_reasoning =
            toolSettings.ai_reasoning !== undefined
              ? toolSettings.ai_reasoning
              : false;
        }
        // Debug: log the payload object
        logger.debug("Payload to backend", { payload });
        abortControllerRef.current = new AbortController(); // Set controller
        await httpPostStream({
          url: `${CHAT_URL}/model`,
          payload,
          onMessage: (chunk, info, data) => {
            // Signal stream end to UI listeners
            if (info === true) {
              if (onStreamMessage) onStreamMessage(chunk, info, data);
              return;
            }
            if (info && info.truncated) {
              // Mark the last assistant message as truncated
              setMessages &&
                setMessages((prev) => {
                  if (!prev.length) return prev;
                  return prev.map((msg, idx) =>
                    idx === prev.length - 1 && msg.type === "assistant"
                      ? { ...msg, truncated: true }
                      : msg
                  );
                });
              return;
            }
            if (onStreamMessage) onStreamMessage(chunk, info, data);
          },
          abortController: abortControllerRef.current, // Pass controller
        });
        // No return value; UI should update via callback
        return;
      } catch (err) {
        // Handle abort error: do not show error bubble, call onAbort
        if (
          err.name === "AbortError" ||
          (typeof err.message === "string" &&
            err.message.toLowerCase().includes("aborted"))
        ) {
          if (onAbort) onAbort();
          return;
        }
        Sentry.captureException(err);
        logger.error("Error sending message", { error: err.message });
        setError(err.response?.data?.error || "Failed to send message");
        throw err;
      } finally {
        setIsChatLoading(false);
        abortControllerRef.current = null; // Reset controller
      }
    },
    []
  );

  // Get all available models
  const getAvailableModels = useCallback(async () => {
    try {
      setError(null);
      logger.info('[useChat] Fetching models', { url: MODEL_URL });
      const response = await httpGet({ url: MODEL_URL });
      logger.info('[useChat] Models fetched successfully', {
        status: response?.status,
        count: response?.data?.length || 0
      });
      return response?.data || [];
    } catch (err) {
      Sentry.captureException(err);
      logger.error("[useChat] Error fetching models", {
        error: err.message,
        status: err.response?.status
      });
      // For anonymous users, return empty array instead of throwing
      // This allows chat to work even if models can't be fetched
      if (err.response?.status === 401) {
        logger.warn('[useChat] 401 on models fetch - returning empty array for anonymous user');
        return [];
      }
      setError("Failed to fetch models");
      throw err;
    }
  }, []);

  // Get models formatted for dropdown
  const getAvailableModelsForDropdown = useCallback(async () => {
    try {
      const models = await getAvailableModels();
      // Check user plan from localStorage
      let filteredModels = models;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user?.plan?.name?.toLowerCase() === "free") {
          filteredModels = models.filter((m) => m.type === "free");
        }
      } catch (e) {
        logger.warn("Error checking user plan for model filtering", { error: e.message });
      }
      return filteredModels;
    } catch (err) {
      Sentry.captureException(err);
      logger.error("Error formatting models for dropdown", { error: err.message });
      return [];
    }
  }, [getAvailableModels]);

  // Get the default model ID
  // Returns empty string to let backend use the auto model (isAuto: true)
  const getDefaultModelId = useCallback(async () => {
    try {
      // Return empty string to trigger backend's auto model selection
      // Backend will use the model marked as isAuto: true in database
      return "";
    } catch (err) {
      Sentry.captureException(err);
      logger.error("Error getting default model ID", { error: err.message });
      return "";
    }
  }, []);

  // Check if a model ID is valid
  const isValidModel = useCallback(
    async (modelId) => {
      try {
        const models = await getAvailableModels();
        return models.some((model) => model._id === modelId);
      } catch (err) {
        Sentry.captureException(err);
        logger.error("Error validating model ID", { error: err.message });
        return false;
      }
    },
    [getAvailableModels]
  );

  const stopGenerating = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    sendMessage,
    getAvailableModels,
    getAvailableModelsForDropdown,
    getDefaultModelId,
    isValidModel,
    isChatLoading,
    error,
    stopGenerating,
  };
};

export default useChat;
